import { guideImage, roomImage } from '@/components/interior-landing/assets';
import { roomSample } from '@/components/interior-landing/pages';
import { findInteriorRoom } from '@/components/interior-landing/rooms';
import type {
  InteriorLandingPage,
  LandingFaq,
  LandingStep,
} from '@/components/interior-landing/types';

import {
  GUIDE_ROOMS,
  type GuideRoom,
  type StyleGuide,
  type StyleGuideCard,
} from './types';

export const STYLES_PATH = '/interior-design-styles';
export const guidePath = (slug: string) => `${STYLES_PATH}/${slug}`;

/** Published / last updated (ISO), for the article data and meta tags. */
export const GUIDES_PUBLISHED = '2026-10-11';

export const GUIDE_ROOM_NAMES: Record<GuideRoom, string> = {
  'living-room': 'Living Room',
  bedroom: 'Bedroom',
  kitchen: 'Kitchen',
};

const room = (slug: GuideRoom) => {
  const found = findInteriorRoom(slug);
  if (!found) throw new Error(`Unknown interior room: ${slug}`);
  return found;
};

/** Our living room, bedroom and kitchen photos, to try a style on. */
const GUIDE_SAMPLES = GUIDE_ROOMS.map((slug) => roomSample(room(slug)));

/** Not shown on the guides (they have no "how it works"), but required. */
const GUIDE_STEPS: [LandingStep, LandingStep, LandingStep] = [
  { title: 'Upload a photo', body: 'Use a photo of your room.' },
  { title: 'Pick a style', body: 'Choose the style you like.' },
  { title: 'Refine and download', body: 'Change details and download.' },
];

const GUIDE_POINTS: [string, string, string] = [
  'Colors, materials and furniture',
  'Tips for every room',
  'Try it on your own photo',
];

export const guideCard = (guide: StyleGuide): StyleGuideCard => ({
  slug: guide.slug,
  name: guide.name,
  tagline: guide.tagline,
  image: guideImage(guide.slug, 'living-room'),
});

/**
 * A guide as a landing page, so it can share the interior pages' hero, start
 * card, before and after view, questions and closing call to action. Every
 * way in opens the AI Interior Designer with the guide's style picked.
 */
export function guidePage(guide: StyleGuide): InteriorLandingPage {
  return {
    id: `style:${guide.slug}`,
    path: guidePath(guide.slug),
    kind: 'guide',
    seoTitle: guide.seoTitle,
    seoDescription: guide.seoDescription,
    ogTitle: `${guide.name} Interior Design`,
    badge: 'Style guide',
    eyebrow: guide.tagline,
    title: `${guide.name} Interior Design,`,
    titleTail: 'explained with real rooms.',
    lead: guide.definition,
    points: GUIDE_POINTS,
    mode: 'restyle',
    roomType: 'Living Room',
    style: guide.toolStyle,
    uploadLabel: `See your room in ${guide.name} style`,
    scratch: 'Describe the room and design it from scratch.',
    samples: GUIDE_SAMPLES,
    scenes: GUIDE_ROOMS.map((slug) => ({
      label: GUIDE_ROOM_NAMES[slug],
      before: roomImage(slug, 'before'),
      looks: [{ label: guide.name, image: guideImage(guide.slug, slug) }],
    })),
    steps: GUIDE_STEPS,
    faqs: guide.faqs,
    breadcrumb: [
      { name: 'Interior Design Styles', path: STYLES_PATH },
      { name: guide.name, path: guidePath(guide.slug) },
    ],
    finalTitle: `See your room in ${guide.name} style.`,
  };
}

/** The guides' index: every style on the same three rooms. */
export function stylesHubPage(
  guides: StyleGuide[],
  faqs: LandingFaq[],
): InteriorLandingPage {
  return {
    id: 'styles',
    path: STYLES_PATH,
    kind: 'guide',
    seoTitle: 'Interior Design Styles: 10 Popular Looks Explained',
    seoDescription:
      'A visual guide to 10 popular interior design styles, from Japandi to Art Deco, each shown on the same rooms. Find your style and try it on your photo.',
    ogTitle: 'Interior Design Styles',
    badge: 'Style guide',
    eyebrow: 'Ten popular styles on the same rooms',
    title: 'Interior Design Styles,',
    titleTail: 'shown on real rooms.',
    lead: 'See ten popular styles on the same living room, bedroom and kitchen, and read what makes each one work: its colors, materials and furniture. When you find one you like, try it on a photo of your own room.',
    points: [
      '10 styles side by side',
      'Colors, materials and furniture',
      'Try any style on your photo',
    ],
    mode: 'restyle',
    roomType: 'Living Room',
    uploadLabel: 'Upload a photo of your room',
    scratch: 'Describe the room and design it from scratch.',
    samples: GUIDE_SAMPLES,
    scenes: GUIDE_ROOMS.map((slug) => ({
      label: GUIDE_ROOM_NAMES[slug],
      before: roomImage(slug, 'before'),
      looks: guides.map((guide) => ({
        label: guide.name,
        image: guideImage(guide.slug, slug),
      })),
    })),
    steps: GUIDE_STEPS,
    faqs,
    breadcrumb: [{ name: 'Interior Design Styles', path: STYLES_PATH }],
    finalTitle: 'Find your style on your own room.',
  };
}
